import React from 'react';
import styled from 'styled-components';
import {
  ModalOverlay,
  ModalPanel,
  ModalTitle,
  ModalBody,
  ModalActions,
  ModalButton,
} from './shared/Modal';
import { useAuth } from '../auth/AuthProvider';

const WELCOME_KEY = 'welcome';

/** Discreet suppress-all link rendered alongside the primary CTA. */
const SuppressLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-right: auto;
  color: #94a3b8;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  text-decoration: underline;

  &:hover { color: #64748b; }
`;

/** First-login welcome modal. Standalone — not a contextual card.
 *  Shows once per user (keyed on the `"welcome"` flag in their prefs).
 *  "Got it" marks it seen. "Don't show first-time tips" sets the master
 *  suppress flag so future cards in this system also stay hidden. */
export const WelcomeModal: React.FC = () => {
  const { isLoading, isAuthenticated, prefs, markSeen, setSuppressAll } = useAuth();

  // Don't even consider rendering until auth+prefs have loaded — otherwise
  // we'd flash the modal then snap it shut once `seen` arrives.
  if (isLoading || !isAuthenticated) return null;
  if (prefs.suppress_all) return null;
  if (prefs.seen.includes(WELCOME_KEY)) return null;

  return (
    <ModalOverlay role="dialog" aria-modal="true" aria-labelledby="welcome-modal-title">
      <ModalPanel $width="480px">
        <ModalTitle id="welcome-modal-title">Welcome to BigPDF</ModalTitle>
        <ModalBody>
          <p style={{ margin: 0 }}>
            This is a toolbox for working with cases of scanned PDFs. It splits
            a big PDF into the documents inside it, lets you fix the splits,
            search by full text, and export the result as separate PDFs.
          </p>
          <p style={{ margin: 0 }}>
            It fits into your intake workflow. When a document&apos;s pages are
            split correctly, mark it <strong>Reviewed</strong> — that confirms
            its boundaries for the team and helps train the model.
          </p>
          <p style={{ margin: 0 }}>
            Each document also gets a <strong>category</strong> from the intake
            checklist, which you can correct or confirm — separately from
            reviewing. Your choice sticks even if the case is re-analyzed.
          </p>
          <p style={{ margin: 0 }}>
            Cases can be linked to <strong>Filevine</strong>: pull documents
            straight from a project, and push the reviewed results back when
            you&apos;re done.
          </p>
          <p style={{ margin: 0 }}>
            Issues or suggestions:{' '}
            <a href="mailto:kevin@fshtechnologies.org">kevin@fshtechnologies.org</a>.
            For bugs, include your browser and OS, what you were doing, and
            any error shown.
          </p>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>
            Short tips will appear as you go.
          </p>
        </ModalBody>
        <ModalActions>
          <SuppressLink
            type="button"
            onClick={() => { void setSuppressAll(true); }}
          >
            Don&apos;t show first-time tips
          </SuppressLink>
          <ModalButton
            type="button"
            $variant="primary"
            onClick={() => { void markSeen(WELCOME_KEY); }}
            autoFocus
          >
            Got it
          </ModalButton>
        </ModalActions>
      </ModalPanel>
    </ModalOverlay>
  );
};

export default WelcomeModal;
